/**
 * Resolve which playback engine powers catalog playback.
 *
 * Order (first match wins):
 * 1. URL ?player=nextgen|legacy|jw|react-player (QA / emergency)
 * 2. localStorage playback_engine_override
 * 3. REACT_APP_PLAYBACK_ENGINE
 * 4. site config configData.playback_engine (explicit only)
 * 5. default: nextgen (UMXP)
 *
 * Deprecated: configData.next_gen_video_player=0 used to force react-player and
 * silently bypassed UMXP. That flag is ignored for engine selection — use
 * playback_engine=legacy|react-player|jw (or query/localStorage) for rollback.
 *
 * Legacy engines remain available until launch stabilization approval.
 */

export var PlaybackEngineId = Object.freeze({
  NEXTGEN: "nextgen",
  /** react-player freeplayer (historical /video/:id) */
  REACT_PLAYER: "react-player",
  /** JW Player (historical /v/:id) */
  JW: "jw",
  /** Alias — prefer react-player path for "legacy" rollback */
  LEGACY: "legacy",
});

function normalize(raw) {
  if (raw == null || raw === "") return null;
  var v = String(raw).trim().toLowerCase();
  if (v === "nextgen" || v === "next-gen" || v === "ngp" || v === "1" || v === "true") {
    return PlaybackEngineId.NEXTGEN;
  }
  if (v === "jw" || v === "jwplayer") {
    return PlaybackEngineId.JW;
  }
  if (
    v === "react-player" ||
    v === "reactplayer" ||
    v === "freeplayer" ||
    v === "legacy" ||
    v === "0" ||
    v === "false"
  ) {
    return PlaybackEngineId.REACT_PLAYER;
  }
  return null;
}

function readQueryOverride() {
  if (typeof window === "undefined") return null;
  try {
    var params = new URLSearchParams(window.location.search || "");
    return normalize(params.get("player") || params.get("playback_engine"));
  } catch (e) {
    return null;
  }
}

function readLocalOverride() {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    return normalize(window.localStorage.getItem("playback_engine_override"));
  } catch (e) {
    return null;
  }
}

function readEnv() {
  return normalize(
    typeof process !== "undefined" &&
      process.env &&
      process.env.REACT_APP_PLAYBACK_ENGINE
  );
}

function readSiteConfig(configuration) {
  if (!configuration || typeof configuration.get !== "function") {
    return null;
  }
  // Explicit engine only — do not treat next_gen_video_player=0 as legacy.
  return normalize(configuration.get("configData.playback_engine"));
}

/**
 * @param {object} [options]
 * @param {object} [options.configuration] react-global-configuration
 * @returns {"nextgen"|"react-player"|"jw"}
 */
export function resolvePlaybackEngine(options) {
  options = options || {};
  return (
    readQueryOverride() ||
    readLocalOverride() ||
    readEnv() ||
    readSiteConfig(options.configuration) ||
    PlaybackEngineId.NEXTGEN
  );
}

/**
 * Whether redirects should target the unified /video/:id host page.
 * JW-only rollback still uses /v/:id for the historical JW route.
 */
export function shouldUseUnifiedVideoRoute(engine) {
  return engine !== PlaybackEngineId.JW;
}

/**
 * Whether a source historically required the legacy react-player embed path.
 * Next-gen UMXP plays YouTube/Vimeo via embed PlaybackProviders; this helper
 * remains for rollback detection / lab tooling — not for Host conditionals.
 *
 * @param {number|string|null|undefined} videoType
 * @param {string|null|undefined} [sourceUrl]
 * @returns {boolean}
 */
export function requiresEmbedPlayer(videoType, sourceUrl) {
  var t = Number(videoType);
  if (t === 2 || t === 4) {
    return true;
  }
  if (sourceUrl && typeof sourceUrl === "string") {
    var u = sourceUrl.toLowerCase();
    if (
      u.indexOf("youtube.com") !== -1 ||
      u.indexOf("youtu.be") !== -1 ||
      u.indexOf("youtube-nocookie.com") !== -1 ||
      u.indexOf("vimeo.com") !== -1 ||
      u.indexOf("player.vimeo.com") !== -1
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Persist a QA override (or clear with null).
 * @param {string|null} engine
 */
export function setPlaybackEngineOverride(engine) {
  if (typeof window === "undefined" || !window.localStorage) return;
  if (engine == null || engine === "") {
    window.localStorage.removeItem("playback_engine_override");
    return;
  }
  var n = normalize(engine);
  if (n) {
    window.localStorage.setItem("playback_engine_override", n);
  }
}

export default resolvePlaybackEngine;
